/**
 * `AskFlintHome` — the landing hub for Ask Flint.
 *
 * Shown at `/ask` (no chatId). Replaces the redirect-to-new-uuid
 * approach so the user lands on a proper home screen instead of
 * immediately opening a blank chat. The screen shows:
 *
 *   1. A prompt input — submitting navigates to `/ask/$chatId` with
 *      the typed text stored in sessionStorage so `ChatBody` can
 *      auto-submit it after mount.
 *   2. "TRY ASKING" — the same four suggestion prompts surfaced in
 *      the empty-state, now clickable from the home screen.
 *   3. Grouped chat history (Today / Yesterday / Last 7 days / Last 30 days)
 *      mirroring the sidebar buckets, but laid out in the main area.
 */
import { useCallback, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowUpIcon,
  GaugeIcon,
  ListChecksIcon,
  ReceiptTextIcon,
  SparklesIcon,
} from "lucide-react";
import {
  PromptInput,
  PromptInputEditor,
  PromptInputToolbar,
  cn,
} from "@flint/ui";
import {
  TryAskingList,
  type TryAskingSuggestion,
} from "../../components/ai-messages/try-asking-list.tsx";
import { useThreads } from "./hooks/use-threads.ts";
import { stashPendingPrompt } from "./pending-chat.ts";
import { ThreadSidebar } from "./thread-sidebar.tsx";

const SUGGESTIONS: readonly TryAskingSuggestion[] = [
  {
    icon: GaugeIcon,
    title: "How does my business look today?",
    prompt:
      "Give me a quick snapshot of my business — outstanding receivables, what is overdue, what I collected this month, and the one thing I should act on first.",
  },
  {
    icon: ReceiptTextIcon,
    title: "Which invoices are overdue?",
    prompt:
      "List my overdue invoices, who they are with, and how many days each is overdue. Suggest who I should chase first.",
  },
  {
    icon: ListChecksIcon,
    title: "Find an invoice for a customer",
    prompt:
      "Find recent invoices for Acme Trading — show document number, amount, due date, and current status.",
  },
  {
    icon: SparklesIcon,
    title: "End-of-day briefing",
    prompt:
      "Give me a short end-of-day briefing: cash position, urgent overdue items, and three follow-ups I should plan for tomorrow.",
  },
];

export function AskFlintHome() {
  const navigate = useNavigate();
  const { threads, setThreads } = useThreads();

  const startChat = useCallback(
    (prompt: string) => {
      const chatId = stashPendingPrompt(prompt);
      void navigate({ to: "/ask/$chatId", params: { chatId } });
    },
    [navigate],
  );

  const handleNewChat = useCallback(() => {
    void navigate({ to: "/ask" });
  }, [navigate]);

  const handleSelectThread = useCallback(
    (id: string) => {
      void navigate({ to: "/ask/$chatId", params: { chatId: id } });
    },
    [navigate],
  );

  const handleThreadDeleted = useCallback(
    (id: string) => {
      setThreads((current) => current.filter((t) => t.id !== id));
    },
    [setThreads],
  );

  return (
    <div className="flex h-full min-h-0 flex-1 overflow-hidden">
      <ThreadSidebar
        activeId=""
        threads={threads}
        onSelect={handleSelectThread}
        onNewChat={handleNewChat}
        onThreadDeleted={handleThreadDeleted}
      />
      {/* Main content */}
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-paper">
        <div className="pointer-events-none absolute inset-0 bg-grid-pattern bg-grid-fade text-slate-400/[0.10]" />

        {/* Scrollable content area */}
        <div className="relative z-10 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[680px] px-4 py-10">
            {/* Hero */}
            <div className="mb-7 text-center">
              <div className="mb-3 flex items-center justify-center gap-2">
                <SparklesIcon className="h-5 w-5 text-sky-500" aria-hidden />
                <span className="text-sm font-semibold uppercase tracking-widest text-sky-500">
                  Ask Flint
                </span>
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-ink">
                What would you like to know?
              </h1>
              <p className="mt-2 text-sm text-muted">
                Ask about invoices, receivables, customers, or your day's cash position.
              </p>
            </div>

            {/* Prompt input */}
            <HomePrompt onSubmit={startChat} />

            {/* Suggestions */}
            <TryAskingList suggestions={SUGGESTIONS} onSelect={startChat} />
          </div>
        </div>
      </div>
    </div>
  );
}

interface HomePromptProps {
  onSubmit: (text: string) => void;
}

function HomePrompt({ onSubmit }: HomePromptProps) {
  const [input, setInput] = useState("");

  function submit() {
    const text = input.trim();
    if (!text) return;
    onSubmit(text);
  }

  return (
    <div>
      <PromptInput
        className={cn(
          "w-full overflow-hidden rounded-lg border border-line bg-paper shadow-card",
          "transition-all duration-200 ease-out",
          "focus-within:border-sky-400 focus-within:shadow-lg focus-within:ring-2 focus-within:ring-sky-100",
        )}
        message={{ text: input, files: [] }}
        onSubmit={(message, event) => {
          event.preventDefault();
          const text = message.text.trim();
          if (!text) return;
          onSubmit(text);
        }}
      >
        <div className="overflow-hidden rounded-lg bg-paper text-base">
          <div className="px-3 py-1.5">
            <PromptInputEditor
              value={input}
              onValueChange={setInput}
              onSubmit={submit}
              placeholder="Ask Flint anything about your business…"
            />
          </div>
          <PromptInputToolbar className="border-t-0 px-3 py-1.5">
            <div />
            <button
              type="submit"
              disabled={!input.trim()}
              className="grid h-8 w-8 place-items-center rounded-lg bg-sky-600 text-white transition-colors hover:bg-sky-700 disabled:opacity-40"
              aria-label="Send"
            >
              <ArrowUpIcon className="h-4 w-4" aria-hidden />
            </button>
          </PromptInputToolbar>
        </div>
      </PromptInput>
      <p className="mt-1.5 text-center text-xs leading-4 text-ink-2">
        Chat output is generated by AI and may not always be accurate. Please verify before taking
        any action.
      </p>
    </div>
  );
}
